import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../supabaseClient";
import { formatAdminDate, getSupabaseErrorMessage } from "./adminConfig";
import { resolveImageUrl } from "../../services/mediaUrl";

const EMPTY_CANONICAL_FORM = {
  name: "",
  brand: "",
  set_name: "",
  subset: "",
  year: "",
  year_end: "",
  card_number: "",
  category_id: "",
  subcategory_id: "",
  additional_details: "",
};

const BULK_CONCURRENCY = 8;
const REVIEW_PAGE_SIZE = 50;

function userItemName(item) {
  return item?.user_specific_name || "Unnamed user item";
}

function userItemImage(item) {
  return item?._resolvedImage || null;
}

function userItemImages(item) {
  if (Array.isArray(item?._resolvedImages) && item._resolvedImages.length) {
    return item._resolvedImages;
  }

  return item?._resolvedImage
    ? [{ image_url: item._resolvedImage, position: 0 }]
    : [];
}

function reviewImageLabel(index) {
  if (index === 0) return "Front";
  if (index === 1) return "Back";
  return `Image ${index + 1}`;
}

function itemKey(value) {
  return String(value ?? "");
}

function attributeValue(item, ...keys) {
  const attributes = item?.attributes;
  if (!attributes || typeof attributes !== "object") return null;

  for (const key of keys) {
    if (
      attributes[key] !== undefined &&
      attributes[key] !== null &&
      attributes[key] !== ""
    ) {
      return attributes[key];
    }
  }

  return null;
}

function suggestedCardNumber(item) {
  return attributeValue(
    item,
    "card_number",
    "cardNumber",
    "number",
    "card_no",
    "cardNo"
  );
}

function suggestedSubset(item) {
  return attributeValue(item, "subset", "sub_set", "subSet");
}

function buildCanonicalForm(item) {
  return {
    name: item?.user_specific_name || "",
    brand: item?.user_specific_brand || "",
    set_name: item?.user_specific_set_name || "",
    subset: suggestedSubset(item) || "",
    year: item?.user_specific_year ?? "",
    year_end: item?.user_specific_year_end ?? "",
    card_number: suggestedCardNumber(item) ?? "",
    category_id: item?.category_id || "",
    subcategory_id: item?.subcategory_id || "",
    additional_details: "",
  };
}

function isSafeMatch(match) {
  return Boolean(
    match &&
      Number(match.candidate_count) === 1 &&
      match.canonical_item_id
  );
}

async function runWithConcurrency(entries, worker, concurrency = BULK_CONCURRENCY) {
  const results = [];
  let cursor = 0;

  async function runner() {
    while (cursor < entries.length) {
      const index = cursor;
      cursor += 1;

      try {
        const value = await worker(entries[index], index);
        results[index] = { ok: true, value };
      } catch (error) {
        results[index] = { ok: false, error };
      }
    }
  }

  await Promise.all(
    Array.from(
      { length: Math.min(concurrency, entries.length || 1) },
      () => runner()
    )
  );

  return results;
}

export default function ItemReviewManager({
  currentUser,
  onPendingCountChange,
  focusUserItemId = null,
  onFocusUserItemHandled,
}) {
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [canonicalForm, setCanonicalForm] = useState(EMPTY_CANONICAL_FORM);

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const [search, setSearch] = useState("");
  const [matchFilter, setMatchFilter] = useState("all");
  const [candidateSearch, setCandidateSearch] = useState("");
  const [reviewPage, setReviewPage] = useState(1);

  const [matchMap, setMatchMap] = useState({});
  const [coverageOpportunities, setCoverageOpportunities] = useState([]);
  const [coverageFilter, setCoverageFilter] = useState(null);
  const [expandedCoverageKey, setExpandedCoverageKey] = useState(null);
  const [showAllCoverage, setShowAllCoverage] = useState(false);
  const [nearMatchMap, setNearMatchMap] = useState({});
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [loading, setLoading] = useState(true);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [matchWarning, setMatchWarning] = useState("");

  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Synchronous guard: React state does not disable a button until the next
  // render, so a fast double-click could otherwise start two bulk requests.
  const bulkActionLock = useRef(false);

  const pendingCount = items.length;

  useEffect(() => {
    onPendingCountChange?.(pendingCount);
  }, [onPendingCountChange, pendingCount]);

  const closeLightbox = useCallback(() => {
    setLightboxImages([]);
    setLightboxIndex(0);
  }, []);

  const openLightbox = useCallback((images, index = 0) => {
    const available = (images || []).filter((image) => image?.image_url);
    if (!available.length) return;

    setLightboxImages(available);
    setLightboxIndex(
      Math.min(Math.max(Number(index) || 0, 0), available.length - 1)
    );
  }, []);

  const showPreviousLightboxImage = useCallback(() => {
    setLightboxIndex((current) =>
      lightboxImages.length
        ? (current - 1 + lightboxImages.length) % lightboxImages.length
        : 0
    );
  }, [lightboxImages.length]);

  const showNextLightboxImage = useCallback(() => {
    setLightboxIndex((current) =>
      lightboxImages.length
        ? (current + 1) % lightboxImages.length
        : 0
    );
  }, [lightboxImages.length]);

  useEffect(() => {
    if (!lightboxImages.length) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeLightbox();
      if (event.key === "ArrowLeft") showPreviousLightboxImage();
      if (event.key === "ArrowRight") showNextLightboxImage();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    lightboxImages.length,
    closeLightbox,
    showPreviousLightboxImage,
    showNextLightboxImage,
  ]);


  const fetchTaxonomy = useCallback(async () => {
    const [categoryResult, subcategoryResult] = await Promise.all([
      supabase.from("categories").select("id, name").order("name"),
      supabase
        .from("subcategories")
        .select("id, name, category_id")
        .order("name"),
    ]);

    if (!categoryResult.error) setCategories(categoryResult.data || []);
    if (!subcategoryResult.error) setSubcategories(subcategoryResult.data || []);
  }, []);

  const fetchMatches = useCallback(async () => {
    setLoadingMatches(true);
    setMatchWarning("");

    const { data, error } = await supabase.rpc(
      "admin_pending_user_item_matches"
    );

    if (error) {
      console.warn("Unable to load exact canonical match summary:", error);
      setMatchMap({});
      setMatchWarning(
        "Bulk exact-match detection is unavailable. Run 01_admin_review_match_helpers.sql, then Refresh."
      );
      setLoadingMatches(false);
      return;
    }

    const next = {};
    (data || []).forEach((row) => {
      const key = itemKey(row.user_item_id);
      const existing = next[key];

      // Ambiguous matches may return more than one physical result row.
      // Collapse them into one non-linkable summary.
      if (!existing) {
        next[key] = row;
      } else if (
        Number(existing.candidate_count) !== 1 ||
        Number(row.candidate_count) !== 1 ||
        existing.canonical_item_id !== row.canonical_item_id
      ) {
        next[key] = {
          user_item_id: key,
          canonical_item_id: null,
          candidate_count: Math.max(
            Number(existing.candidate_count || 0),
            Number(row.candidate_count || 0),
            2
          ),
        };
      }
    });

    setMatchMap(next);
    setSelectedIds((current) => {
      const safeKeys = new Set(
        Object.entries(next)
          .filter(([, match]) => isSafeMatch(match))
          .map(([key]) => key)
      );

      return new Set(
        [...current].filter((key) => safeKeys.has(itemKey(key)))
      );
    });

    setLoadingMatches(false);
  }, []);

  const fetchCoverage = useCallback(async () => {
    const { data, error } = await supabase.rpc(
      "admin_item_review_coverage_opportunities",
      { p_limit: 1000 }
    );

    if (error) {
      console.warn("Unable to load canonical coverage opportunities:", error);
      setCoverageOpportunities([]);
      return;
    }

    setCoverageOpportunities(data || []);
  }, []);

  const fetchItems = useCallback(async ({ preserveMessages = false } = {}) => {
    setLoading(true);
    if (!preserveMessages) setErrorMessage("");

    const { data, error } = await supabase
      .from("user_items")
      .select("*")
      .is("item_id", null)
      .eq("status", "Pending")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Unable to load unlinked user items:", error);
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to load item review queue.")
      );
      setLoading(false);
      return;
    }

    const rows = data || [];
    setItems(rows);

    setSelectedItem((current) => {
      if (!current) return null;
      return rows.find((row) => itemKey(row.id) === itemKey(current.id)) || null;
    });

    setSelectedIds((current) => {
      const live = new Set(rows.map((row) => itemKey(row.id)));
      return new Set([...current].filter((key) => live.has(itemKey(key))));
    });

    setLoading(false);
    await Promise.all([fetchMatches(), fetchCoverage()]);
  }, [fetchMatches, fetchCoverage]);

  useEffect(() => {
    fetchItems();
    fetchTaxonomy();
  }, [fetchItems, fetchTaxonomy]);

  const openItem = useCallback(async (item) => {
    setSelectedItem(item);
    setCanonicalForm(buildCanonicalForm(item));
    setCandidateSearch(item?.user_specific_name || "");
    setCandidates([]);
    setErrorMessage("");
    setStatusMessage("");

    const key = itemKey(item?.id);
    if (!key) return;

    // Images are intentionally loaded only after an admin opens Review.
    // This keeps the queue itself from requesting every pending item's
    // full-resolution Storage image and consuming unnecessary egress.
    const { data: imageRows, error: imageError } = await supabase
      .from("item_images")
      .select("user_item_id, position, storage_provider, storage_bucket, object_key")
      .eq("user_item_id", item.id)
      .order("position", { ascending: true });

    if (imageError) {
      console.warn("Unable to load images for selected review item:", imageError);
    } else {
      const resolvedImages = (imageRows || [])
        .map((image, index) => ({
          ...image,
          image_url: resolveImageUrl(image),
          position: image.position ?? index,
        }))
        .filter((image) => image.image_url);

      setSelectedItem((current) =>
        current && itemKey(current.id) === key
          ? {
              ...current,
              _resolvedImages: resolvedImages,
              _resolvedImage: resolvedImages[0]?.image_url || null,
            }
          : current
      );
    }

    if (isSafeMatch(matchMap[key]) || nearMatchMap[key]) return;

    const { data, error } = await supabase.rpc(
      "admin_user_item_near_matches_v2",
      {
        p_user_item_id: item.id,
        p_limit: 10,
      }
    );

    if (error) {
      console.warn("Unable to load near canonical matches:", error);
      return;
    }

    setNearMatchMap((current) => ({
      ...current,
      [key]: data || [],
    }));
  }, [matchMap, nearMatchMap]);

  useEffect(() => {
    if (focusUserItemId === null || focusUserItemId === undefined || focusUserItemId === "") return;

    let cancelled = false;

    const focusRequestedItem = async () => {
      setLoading(true);
      setErrorMessage("");

      const { data, error } = await supabase
        .from("user_items")
        .select("*")
        .eq("id", focusUserItemId)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        console.error("Unable to load requested user item from Variant Review:", error);
        setErrorMessage(
          getSupabaseErrorMessage(error, "Unable to load the requested user item.")
        );
        setLoading(false);
        onFocusUserItemHandled?.();
        return;
      }

      if (!data) {
        setErrorMessage(`User item #${focusUserItemId} could not be found.`);
        setLoading(false);
        onFocusUserItemHandled?.();
        return;
      }

      // A Variant Review handoff may point to an item that is already linked,
      // so surface it even when it is outside the normal Pending/unlinked queue.
      setItems((current) => {
        const key = itemKey(data.id);
        if (current.some((row) => itemKey(row.id) === key)) return current;
        return [data, ...current];
      });

      setLoading(false);
      await openItem(data);
      onFocusUserItemHandled?.();
    };

    focusRequestedItem();

    return () => {
      cancelled = true;
    };
  }, [focusUserItemId, onFocusUserItemHandled, openItem]);

  const searchCanonical = useCallback(async () => {
    if (!selectedItem) return;

    setLoadingCandidates(true);
    setErrorMessage("");

    const term = candidateSearch.trim();

    let query = supabase
      .from("items")
      .select(
        "id, name, brand, set_name, subset, year, card_number, category_id, subcategory_id, release_id"
      )
      .limit(25);

    if (term) {
      query = query.ilike(
        "name",
        `%${term.replace(/[(),]/g, " ").trim()}%`
      );
    }

    const { data, error } = await query;

    if (error) {
      console.error("Unable to search canonical items:", error);
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to search canonical items.")
      );
      setCandidates([]);
      setLoadingCandidates(false);
      return;
    }

    const candidateRows = data || [];
    const releaseIds = [
      ...new Set(candidateRows.map((candidate) => candidate.release_id).filter(Boolean)),
    ];

    let releaseById = {};
    let familyReleasesByFamilyId = {};

    if (releaseIds.length) {
      const { data: releaseRows, error: releaseError } = await supabase
        .from("card_releases")
        .select("id, set_name, brand, year, release_family_id")
        .in("id", releaseIds);

      if (releaseError) {
        console.warn("Unable to load modeled releases for canonical search:", releaseError);
      } else {
        releaseById = Object.fromEntries(
          (releaseRows || []).map((release) => [itemKey(release.id), release])
        );

        const familyIds = [
          ...new Set(
            (releaseRows || [])
              .map((release) => release.release_family_id)
              .filter(Boolean)
          ),
        ];

        if (familyIds.length) {
          const { data: familyReleaseRows, error: familyReleaseError } = await supabase
            .from("card_releases")
            .select("id, set_name, brand, year, release_family_id")
            .in("release_family_id", familyIds);

          if (familyReleaseError) {
            console.warn(
              "Unable to load release-family context for canonical search:",
              familyReleaseError
            );
          } else {
            familyReleasesByFamilyId = (familyReleaseRows || []).reduce(
              (groups, release) => {
                const key = itemKey(release.release_family_id);
                if (!groups[key]) groups[key] = [];
                groups[key].push(release);
                return groups;
              },
              {}
            );
          }
        }
      }
    }

    const normalizeIdentityText = (value) =>
      String(value ?? "").trim().toLowerCase();

    const source = selectedItem;
    const sourceName = normalizeIdentityText(source.user_specific_name);
    const sourceBrand = normalizeIdentityText(source.user_specific_brand);
    const sourceSet = normalizeIdentityText(source.user_specific_set_name);
    const sourceSubset = normalizeIdentityText(suggestedSubset(source));
    const sourceYear = source.user_specific_year;
    const sourceNumber = suggestedCardNumber(source);

    const rows = candidateRows.map((candidate) => {
      let score = 0;

      const candidateRelease = releaseById[itemKey(candidate.release_id)] || null;
      const familyReleases = candidateRelease?.release_family_id
        ? familyReleasesByFamilyId[itemKey(candidateRelease.release_family_id)] || []
        : [];

      const releaseMatchesSubmittedSubset = Boolean(
        sourceSubset &&
          candidateRelease &&
          normalizeIdentityText(candidateRelease.set_name) === sourceSubset
      );

      const familyContainsSubmittedSet = Boolean(
        sourceSet &&
          candidateRelease?.release_family_id &&
          familyReleases.some((release) => {
            if (normalizeIdentityText(release.set_name) !== sourceSet) return false;
            if (
              sourceYear &&
              release.year !== null &&
              release.year !== undefined &&
              String(release.year) !== String(sourceYear)
            ) return false;
            if (
              sourceBrand &&
              release.brand &&
              normalizeIdentityText(release.brand) !== sourceBrand
            ) return false;
            return true;
          })
      );

      if (sourceName && normalizeIdentityText(candidate.name) === sourceName) score += 5;
      if (sourceBrand && normalizeIdentityText(candidate.brand) === sourceBrand) score += 2;
      if (sourceSet && normalizeIdentityText(candidate.set_name) === sourceSet) score += 2;
      if (sourceSubset && normalizeIdentityText(candidate.subset) === sourceSubset) score += 2;
      if (sourceYear && String(candidate.year) === String(sourceYear)) score += 2;
      if (sourceNumber && String(candidate.card_number) === String(sourceNumber)) score += 3;

      // Mirror the server-side V4 family/release interpretation strongly enough
      // that legacy "product + insert" submissions rank the modeled release first.
      if (familyContainsSubmittedSet && releaseMatchesSubmittedSubset) score += 7;
      else if (releaseMatchesSubmittedSubset) score += 3;
      else if (familyContainsSubmittedSet) score += 2;

      return {
        ...candidate,
        _matchScore: score,
        _familyContainsSubmittedSet: familyContainsSubmittedSet,
        _releaseMatchesSubmittedSubset: releaseMatchesSubmittedSubset,
      };
    });

    rows.sort((a, b) =>
      b._matchScore - a._matchScore ||
      Number(b._familyContainsSubmittedSet && b._releaseMatchesSubmittedSubset) -
        Number(a._familyContainsSubmittedSet && a._releaseMatchesSubmittedSubset) ||
      Number(b._releaseMatchesSubmittedSubset) - Number(a._releaseMatchesSubmittedSubset) ||
      Number(Boolean(b.release_id)) - Number(Boolean(a.release_id)) ||
      Number(a.id) - Number(b.id)
    );

    setCandidates(rows);
    setLoadingCandidates(false);
  }, [candidateSearch, selectedItem]);

  const approveExistingLink = useCallback(
    async (item, canonicalItem, { confirm = true } = {}) => {
      if (!item || !canonicalItem) return { ok: false };

      if (
        confirm &&
        !window.confirm(
          `Approve and link ${userItemName(item)} to ${canonicalItem.name}?`
        )
      ) {
        return { ok: false, cancelled: true };
      }

      const { data, error } = await supabase.rpc("approve_user_item", {
        p_user_item_id: item.id,
        p_canonical_item_id: canonicalItem.id,
        p_canonical_payload: null,
      });

      if (error) throw error;
      return { ok: true, data };
    },
    []
  );

  const linkToCanonical = async (canonicalItem) => {
    if (!selectedItem) return;

    setBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    try {
      const result = await approveExistingLink(selectedItem, canonicalItem);
      if (result.cancelled) {
        setBusy(false);
        return;
      }

      const linkedId = itemKey(selectedItem.id);
      setItems((prev) =>
        prev.filter((item) => itemKey(item.id) !== linkedId)
      );
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(linkedId);
        return next;
      });
      setSelectedItem(null);
      setCandidates([]);
      setStatusMessage(
        `Approved ${userItemName(selectedItem)} and linked it to ${
          result.data?.canonical_name || canonicalItem.name
        }.`
      );
      await fetchMatches();
    } catch (error) {
      console.error("Unable to approve/link user item:", error);
      setErrorMessage(
        getSupabaseErrorMessage(
          error,
          "Unable to approve and link user item."
        )
      );
    }

    setBusy(false);
  };

  const quickLinkSafeMatch = async (item) => {
    const match = matchMap[itemKey(item.id)];
    if (!isSafeMatch(match)) return;

    const canonicalItem = {
      id: match.canonical_item_id,
      name: match.canonical_name,
    };

    if (
      !window.confirm(
        `Link ${userItemName(item)} to exact canonical match ${match.canonical_name}?`
      )
    ) return;

    setBulkBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    try {
      await approveExistingLink(item, canonicalItem, { confirm: false });
      const key = itemKey(item.id);
      setItems((prev) => prev.filter((row) => itemKey(row.id) !== key));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setStatusMessage(
        `Linked ${userItemName(item)} to ${match.canonical_name}.`
      );
      await fetchMatches();
    } catch (error) {
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to link the exact match.")
      );
    }

    setBulkBusy(false);
  };

  const createCanonicalAndLink = async () => {
    if (!selectedItem) return;

    if (!currentUser?.id) {
      setErrorMessage("The current admin user ID is missing.");
      return;
    }

    const name = canonicalForm.name.trim();
    if (!name) {
      setErrorMessage("A canonical item name is required.");
      return;
    }

    if (
      !window.confirm(
        `Create a new canonical item for ${name} and approve this user item?`
      )
    ) return;

    setBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    const canonicalPayload = {
      name,
      brand: canonicalForm.brand.trim() || null,
      set_name: canonicalForm.set_name.trim() || null,
      subset: canonicalForm.subset.trim() || null,
      year:
        canonicalForm.year === ""
          ? null
          : Number(canonicalForm.year),
      year_end:
        canonicalForm.year_end === ""
          ? null
          : Number(canonicalForm.year_end),
      card_number: canonicalForm.card_number.trim() || null,
      category_id: canonicalForm.category_id || null,
      subcategory_id: canonicalForm.subcategory_id || null,
      additional_details:
        canonicalForm.additional_details.trim() || null,
    };

    const { data, error } = await supabase.rpc("approve_user_item", {
      p_user_item_id: selectedItem.id,
      p_canonical_item_id: null,
      p_canonical_payload: canonicalPayload,
    });

    if (error) {
      console.error(
        "Unable to create canonical item and approve submission:",
        error
      );
      setErrorMessage(
        getSupabaseErrorMessage(
          error,
          "Unable to create canonical item and approve submission."
        )
      );
      setBusy(false);
      return;
    }

    const createdId = itemKey(selectedItem.id);
    setItems((prev) =>
      prev.filter((item) => itemKey(item.id) !== createdId)
    );
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(createdId);
      return next;
    });
    setSelectedItem(null);
    setCandidates([]);
    setStatusMessage(
      `Created canonical item ${data?.canonical_name || name} and approved the user item.`
    );
    await fetchMatches();
    setBusy(false);
  };

  const denyItem = async () => {
    if (!selectedItem) return;

    if (
      !window.confirm(
        `Deny ${userItemName(selectedItem)}? It will leave the admin queue until the user edits it.`
      )
    ) return;

    setBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    const deniedId = selectedItem.id;
    const deniedName = userItemName(selectedItem);

    const { error } = await supabase.rpc("deny_user_item", {
      p_user_item_id: deniedId,
    });

    if (error) {
      console.error("Unable to deny user item:", error);
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to deny user item.")
      );
      setBusy(false);
      return;
    }

    const deniedKey = itemKey(deniedId);
    setItems((prev) =>
      prev.filter((item) => itemKey(item.id) !== deniedKey)
    );
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(deniedKey);
      return next;
    });
    setSelectedItem(null);
    setCandidates([]);
    setStatusMessage(
      `Denied ${deniedName}. It will return to review if the user edits it.`
    );
    await fetchMatches();
    setBusy(false);
  };

  const coverageByYearSport = useMemo(() => {
    const grouped = new Map();

    coverageOpportunities.forEach((group) => {
      const key = `${group.year ?? "unknown"}::${group.subcategory_id ?? "unknown"}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          year: group.year,
          subcategory_id: group.subcategory_id,
          subcategory_name: group.subcategory_name,
          pending_count: 0,
          missing_count: 0,
          existing_count: 0,
          ambiguous_count: 0,
          groups: [],
        });
      }

      const bucket = grouped.get(key);
      const count = Number(group.pending_count || 0);
      bucket.pending_count += count;
      bucket.groups.push(group);
      if (group.family_status === "missing_family") bucket.missing_count += count;
      else if (group.family_status === "existing_family") bucket.existing_count += count;
      else if (group.family_status === "ambiguous_family") bucket.ambiguous_count += count;
    });

    return [...grouped.values()].sort((a, b) =>
      b.pending_count - a.pending_count ||
      Number(b.year || 0) - Number(a.year || 0) ||
      String(a.subcategory_name || "").localeCompare(String(b.subcategory_name || ""))
    );
  }, [coverageOpportunities]);

  const coverageTotals = useMemo(() =>
    coverageByYearSport.reduce(
      (totals, bucket) => ({
        pending: totals.pending + bucket.pending_count,
        missing: totals.missing + bucket.missing_count,
        existing: totals.existing + bucket.existing_count,
        ambiguous: totals.ambiguous + bucket.ambiguous_count,
      }),
      { pending: 0, missing: 0, existing: 0, ambiguous: 0 }
    ),
    [coverageByYearSport]
  );

  const visibleCoverageBuckets = useMemo(() =>
    showAllCoverage ? coverageByYearSport : coverageByYearSport.slice(0, 5),
    [coverageByYearSport, showAllCoverage]
  );

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();

    return items.filter((item) => {
      const match = matchMap[itemKey(item.id)];
      const safe = isSafeMatch(match);
      const ambiguous = Number(match?.candidate_count || 0) > 1;

      if (coverageFilter) {
        const sameYear =
          String(item.user_specific_year ?? "") === String(coverageFilter.year ?? "");
        const sameSubcategory =
          itemKey(item.subcategory_id) === itemKey(coverageFilter.subcategory_id);

        if (!(sameYear && sameSubcategory)) return false;

        if (coverageFilter.type === "family") {
          const sameBrand =
            String(item.user_specific_brand ?? "").trim().toLowerCase() ===
            String(coverageFilter.submitted_brand ?? "").trim().toLowerCase();
          const sameSet =
            String(item.user_specific_set_name ?? "").trim().toLowerCase() ===
            String(coverageFilter.submitted_set ?? "").trim().toLowerCase();
          if (!(sameBrand && sameSet)) return false;
        }
      }

      if (matchFilter === "safe" && !safe) return false;
      if (matchFilter === "review" && (safe || ambiguous)) return false;
      if (matchFilter === "ambiguous" && !ambiguous) return false;

      if (!term) return true;

      return [
        item.user_specific_name,
        item.user_specific_brand,
        item.user_specific_set_name,
        suggestedSubset(item),
        item.user_specific_year,
        suggestedCardNumber(item),
        item.description,
        match?.canonical_name,
        match?.canonical_brand,
        match?.canonical_set_name,
        match?.canonical_subset,
        match?.canonical_year,
        match?.canonical_card_number,
      ]
        .filter((value) => value !== undefined && value !== null)
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [items, search, matchFilter, matchMap, coverageFilter]);

  const totalReviewPages = Math.max(1, Math.ceil(filteredItems.length / REVIEW_PAGE_SIZE));

  useEffect(() => {
    setReviewPage((current) => Math.min(Math.max(current, 1), totalReviewPages));
  }, [totalReviewPages]);

  useEffect(() => {
    setReviewPage(1);
  }, [search, matchFilter, coverageFilter]);

  const paginatedItems = useMemo(() => {
    const start = (reviewPage - 1) * REVIEW_PAGE_SIZE;
    return filteredItems.slice(start, start + REVIEW_PAGE_SIZE);
  }, [filteredItems, reviewPage]);

  const safeVisibleItems = useMemo(
    () =>
      paginatedItems.filter((item) =>
        isSafeMatch(matchMap[itemKey(item.id)])
      ),
    [paginatedItems, matchMap]
  );

  const safeTotal = useMemo(
    () =>
      items.filter((item) => isSafeMatch(matchMap[itemKey(item.id)])).length,
    [items, matchMap]
  );

  const ambiguousTotal = useMemo(
    () =>
      items.filter(
        (item) => Number(matchMap[itemKey(item.id)]?.candidate_count || 0) > 1
      ).length,
    [items, matchMap]
  );

  const needsReviewTotal = Math.max(0, items.length - safeTotal - ambiguousTotal);

  const selectedSafeItems = useMemo(
    () =>
      items.filter(
        (item) =>
          selectedIds.has(itemKey(item.id)) &&
          isSafeMatch(matchMap[itemKey(item.id)])
      ),
    [items, selectedIds, matchMap]
  );

  const allVisibleSafeSelected =
    safeVisibleItems.length > 0 &&
    safeVisibleItems.every((item) => selectedIds.has(itemKey(item.id)));

  const toggleSelected = (item) => {
    const key = itemKey(item.id);
    const match = matchMap[key];
    if (!isSafeMatch(match)) return;

    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllVisibleSafe = () => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (allVisibleSafeSelected) {
        safeVisibleItems.forEach((item) => next.delete(itemKey(item.id)));
      } else {
        safeVisibleItems.forEach((item) => next.add(itemKey(item.id)));
      }
      return next;
    });
  };

  const selectAllSafe = () => {
    setSelectedIds(
      new Set(
        items
          .filter((item) => isSafeMatch(matchMap[itemKey(item.id)]))
          .map((item) => itemKey(item.id))
      )
    );
    setCoverageFilter(null);
    setMatchFilter("safe");
    setReviewPage(1);
  };

  const bulkLinkSelected = async () => {
    if (!selectedSafeItems.length || bulkActionLock.current) return;

    if (
      !window.confirm(
        `Link ${selectedSafeItems.length} selected exact match${
          selectedSafeItems.length === 1 ? "" : "es"
        } to canonical items?\n\nOnly rows with one exact canonical identity match are included.`
      )
    ) return;

    // Lock synchronously before any await or React re-render.
    bulkActionLock.current = true;
    setBulkBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    try {
      const links = selectedSafeItems.map((item) => {
        const match = matchMap[itemKey(item.id)];

        return {
          user_item_id: item.id,
          canonical_item_id: match.canonical_item_id,
        };
      });

      const { data, error } = await supabase.rpc(
        "bulk_approve_user_item_links",
        {
          p_links: links,
        }
      );

      if (error) throw error;

      const linkedCount = Number(data?.linked_count || 0);
      const alreadyLinkedCount = Number(data?.already_linked_count || 0);
      const failedCount = Number(data?.failed_count || 0);
      const processedCount = linkedCount + alreadyLinkedCount;

      if (failedCount > 0) {
        const firstFailure = (data?.results || []).find(
          (result) => result.status === "failed"
        );

        setErrorMessage(
          `${processedCount} item${
            processedCount === 1 ? "" : "s"
          } linked/already linked; ${failedCount} could not be processed.${
            firstFailure?.message
              ? ` First issue: ${firstFailure.message}`
              : ""
          } The queue has been refreshed from Supabase.`
        );
      } else {
        setStatusMessage(
          `Processed ${processedCount} exact match${
            processedCount === 1 ? "" : "es"
          }: ${linkedCount} newly linked${
            alreadyLinkedCount
              ? `, ${alreadyLinkedCount} already linked`
              : ""
          }.`
        );
      }

      // Do not trust the old browser snapshot after a bulk operation.
      // Re-read the real pending queue from Supabase.
      setSelectedIds(new Set());
      await fetchItems({ preserveMessages: true });
    } catch (error) {
      console.error("Unable to bulk-link canonical matches:", error);
      setErrorMessage(
        getSupabaseErrorMessage(
          error,
          "Unable to bulk-link the selected canonical matches."
        )
      );

      // Even on an error, refresh because a prior/overlapping request may
      // already have changed some rows.
      await fetchItems({ preserveMessages: true });
    } finally {
      bulkActionLock.current = false;
      setBulkBusy(false);
    }
  };

  const categoryNameById = useMemo(
    () =>
      Object.fromEntries(
        categories.map((category) => [itemKey(category.id), category.name])
      ),
    [categories]
  );

  const subcategoryNameById = useMemo(
    () =>
      Object.fromEntries(
        subcategories.map((subcategory) => [
          itemKey(subcategory.id),
          subcategory.name,
        ])
      ),
    [subcategories]
  );

  const formSubcategories = useMemo(
    () =>
      subcategories.filter(
        (subcategory) =>
          !canonicalForm.category_id ||
          itemKey(subcategory.category_id) ===
            itemKey(canonicalForm.category_id)
      ),
    [subcategories, canonicalForm.category_id]
  );

  return (
    <div>
      <div
        className="hadmin-toolbar"
        style={{ alignItems: "center", flexWrap: "wrap", gap: 10 }}
      >
        <input
          className="hadmin-input hadmin-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search pending user items or proposed matches..."
        />

        <select
          className="hadmin-input"
          value={matchFilter}
          onChange={(event) => setMatchFilter(event.target.value)}
          style={{ minWidth: 180 }}
        >
          <option value="all">All pending</option>
          <option value="safe">Exact matches</option>
          <option value="review">No exact match</option>
          <option value="ambiguous">Ambiguous matches</option>
        </select>

        <button
          type="button"
          className="hadmin-btn-secondary"
          onClick={fetchItems}
          disabled={loading || bulkBusy}
        >
          {loading || loadingMatches ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="hadmin-info" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {[
          ["all", `${items.length} Pending`],
          ["safe", `${safeTotal} Exact`],
          ["ambiguous", `${ambiguousTotal} Ambiguous`],
          ["review", `${needsReviewTotal} Needs Review`],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={matchFilter === value && !coverageFilter ? "hadmin-btn-success" : "hadmin-btn-secondary"}
            onClick={() => {
              setCoverageFilter(null);
              setMatchFilter(value);
            }}
          >
            {label}
          </button>
        ))}
        <span style={{ fontSize: 12, opacity: 0.78 }}>
          Exact matches have one safe canonical identity; ambiguous rows are never bulk-linked automatically.
        </span>
      </div>

      {matchWarning && <div className="hadmin-error">{matchWarning}</div>}
      {errorMessage && <div className="hadmin-error">{errorMessage}</div>}
      {statusMessage && <div className="hadmin-success">{statusMessage}</div>}

      <section className="hadmin-panel" style={{ marginBottom: 12, padding: 12 }}>
        <div className="hadmin-row-between" style={{ gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <h3 style={{ margin: 0 }}>Canonical Coverage Demand</h3>
            <div className="hadmin-muted" style={{ marginTop: 4, fontSize: 12 }}>
              {coverageTotals.pending} unresolved · {coverageTotals.missing} missing-family · {coverageTotals.existing} existing-family · {coverageTotals.ambiguous} ambiguous
            </div>
          </div>
          {coverageByYearSport.length > 5 && (
            <button
              type="button"
              className="hadmin-btn-secondary"
              onClick={() => {
                setShowAllCoverage((current) => !current);
                if (showAllCoverage) setExpandedCoverageKey(null);
              }}
            >
              {showAllCoverage ? "Show Top 5" : `View All (${coverageByYearSport.length})`}
            </button>
          )}
        </div>

        {!coverageByYearSport.length ? (
          <div className="hadmin-empty" style={{ marginTop: 10 }}>No grouped coverage demand is available right now.</div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 10 }}>
              {visibleCoverageBuckets.map((bucket) => {
                const expanded = expandedCoverageKey === bucket.key;
                const yearActive =
                  coverageFilter?.type === "year" &&
                  String(coverageFilter.year ?? "") === String(bucket.year ?? "") &&
                  itemKey(coverageFilter.subcategory_id) === itemKey(bucket.subcategory_id);

                return (
                  <button
                    key={bucket.key}
                    type="button"
                    className={yearActive || expanded ? "hadmin-btn-success" : "hadmin-btn-secondary"}
                    onClick={() => {
                      setExpandedCoverageKey(expanded ? null : bucket.key);
                      setCoverageFilter(yearActive ? null : {
                        type: "year",
                        year: bucket.year,
                        subcategory_id: bucket.subcategory_id,
                      });
                      setMatchFilter("review");
                    }}
                    title={`${bucket.missing_count} missing-family · ${bucket.existing_count} existing-family · ${bucket.ambiguous_count} ambiguous`}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {bucket.year || "Unknown"} {bucket.subcategory_name || "Unknown"} · {bucket.pending_count}
                  </button>
                );
              })}
            </div>

            {expandedCoverageKey && (() => {
              const bucket = coverageByYearSport.find((entry) => entry.key === expandedCoverageKey);
              if (!bucket) return null;
              return (
                <div style={{ marginTop: 10, borderTop: "1px solid rgba(127,127,127,.22)", paddingTop: 9 }}>
                  <div className="hadmin-row-between" style={{ gap: 8, flexWrap: "wrap", marginBottom: 7 }}>
                    <strong>{bucket.year || "Unknown year"} · {bucket.subcategory_name || "Unknown sport"} — {bucket.pending_count} unresolved</strong>
                    <span className="hadmin-muted" style={{ fontSize: 11 }}>
                      {bucket.missing_count} missing · {bucket.existing_count} existing · {bucket.ambiguous_count} ambiguous
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                    {bucket.groups
                      .slice()
                      .sort((a, b) => Number(b.pending_count || 0) - Number(a.pending_count || 0))
                      .map((group, index) => {
                        const familyActive =
                          coverageFilter?.type === "family" &&
                          String(coverageFilter.year ?? "") === String(group.year ?? "") &&
                          itemKey(coverageFilter.subcategory_id) === itemKey(group.subcategory_id) &&
                          String(coverageFilter.submitted_brand ?? "").toLowerCase() === String(group.submitted_brand ?? "").toLowerCase() &&
                          String(coverageFilter.submitted_set ?? "").toLowerCase() === String(group.submitted_set ?? "").toLowerCase();
                        const label = group.family_status === "existing_family"
                          ? group.family_name
                          : `${group.submitted_brand || "Unknown brand"}${group.submitted_set ? ` ${group.submitted_set}` : ""}`;
                        const marker = group.family_status === "existing_family" ? "existing" : group.family_status === "ambiguous_family" ? "ambiguous" : "missing";
                        return (
                          <button
                            key={`${bucket.key}-${group.submitted_brand}-${group.submitted_set}-${index}`}
                            type="button"
                            className={familyActive ? "hadmin-btn-success" : "hadmin-btn-secondary"}
                            onClick={() => {
                              setCoverageFilter(familyActive ? { type: "year", year: bucket.year, subcategory_id: bucket.subcategory_id } : { ...group, type: "family" });
                              setMatchFilter("review");
                            }}
                            title={`${group.pending_count} unresolved · ${marker} family`}
                            style={{ whiteSpace: "normal", textAlign: "left" }}
                          >
                            {label || "Unmapped family"} · {group.pending_count}
                          </button>
                        );
                      })}
                    <button
                      type="button"
                      className="hadmin-btn-secondary"
                      onClick={() => setExpandedCoverageKey(null)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              );
            })()}
          </>
        )}

        {coverageFilter && (
          <div style={{ marginTop: 8 }}>
            <button type="button" className="hadmin-btn-secondary" onClick={() => { setCoverageFilter(null); setExpandedCoverageKey(null); }}>
              Clear Coverage Filter
            </button>
          </div>
        )}
      </section>

      <section className="hadmin-panel">
        <div
          className="hadmin-row-between"
          style={{ marginBottom: 14, gap: 12, flexWrap: "wrap" }}
        >
          <div>
            <strong>
              {items.length} item{items.length === 1 ? "" : "s"} awaiting canonical review
            </strong>
            <div className="hadmin-muted" style={{ marginTop: 3, fontSize: 12 }}>
              {paginatedItems.length} shown on this page · {filteredItems.length} matching total · {selectedSafeItems.length} exact match
              {selectedSafeItems.length === 1 ? "" : "es"} selected
            </div>
          </div>

          <div className="hadmin-actions" style={{ flexWrap: "wrap" }}>
            <button
              type="button"
              className="hadmin-btn-secondary"
              onClick={selectAllSafe}
              disabled={!safeTotal || bulkBusy}
            >
              Select All {safeTotal} Exact
            </button>

            <button
              type="button"
              className="hadmin-btn-success"
              onClick={bulkLinkSelected}
              disabled={!selectedSafeItems.length || bulkBusy}
            >
              {bulkBusy
                ? "Linking..."
                : `Link ${selectedSafeItems.length || ""} Selected Match${
                    selectedSafeItems.length === 1 ? "" : "es"
                  }`}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="hadmin-empty">Loading item review queue...</div>
        ) : !filteredItems.length ? (
          <div className="hadmin-empty">
            No pending user items match the current filter.
          </div>
        ) : (
          <div className="hadmin-table-wrap">
            <table className="hadmin-table">
              <thead>
                <tr>
                  <th style={{ width: 42 }}>
                    <input
                      type="checkbox"
                      checked={allVisibleSafeSelected}
                      onChange={toggleAllVisibleSafe}
                      disabled={!safeVisibleItems.length || bulkBusy}
                      title="Select all exact matches currently shown"
                    />
                  </th>
                  <th>User Item</th>
                  <th>Submitted Identity</th>
                  <th>Canonical Match</th>
                  <th>Added</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {paginatedItems.map((item) => {
                  const key = itemKey(item.id);
                  const match = matchMap[key];
                  const safe = isSafeMatch(match);
                  const ambiguous = Number(match?.candidate_count || 0) > 1;

                  return (
                    <tr
                      key={item.id}
                      className={selectedItem?.id === item.id ? "selected" : ""}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(key)}
                          disabled={!safe || bulkBusy}
                          onChange={() => toggleSelected(item)}
                          title={
                            safe
                              ? "Select this exact match"
                              : "Only single exact matches can be bulk-linked"
                          }
                        />
                      </td>


                      <td>
                        <strong>{userItemName(item)}</strong>
                        <div className="hadmin-muted" style={{ marginTop: 4 }}>
                          User item #{item.id}
                        </div>
                      </td>

                      <td>
                        <div>
                          {[
                            item.user_specific_year,
                            item.user_specific_brand,
                            item.user_specific_set_name,
                            suggestedSubset(item),
                          ]
                            .filter(Boolean)
                            .join(" • ") || "—"}
                        </div>
                        <div className="hadmin-muted" style={{ marginTop: 4 }}>
                          #{suggestedCardNumber(item) ?? "—"}
                        </div>
                        <div className="hadmin-muted" style={{ marginTop: 3, fontSize: 11 }}>
                          {categoryNameById[itemKey(item.category_id)] || item.category_id || "—"}
                          {" / "}
                          {subcategoryNameById[itemKey(item.subcategory_id)] ||
                            item.subcategory_id ||
                            "—"}
                        </div>
                      </td>

                      <td>
                        {safe ? (
                          <div>
                            <strong>{match.canonical_name}</strong>
                            <div className="hadmin-muted" style={{ marginTop: 4 }}>
                              {[
                                match.canonical_year,
                                match.canonical_brand,
                                match.canonical_set_name,
                                match.canonical_subset,
                              ]
                                .filter(Boolean)
                                .join(" • ")}
                            </div>
                            <div className="hadmin-muted" style={{ marginTop: 3 }}>
                              #{match.canonical_card_number ?? "—"} · canonical #
                              {match.canonical_item_id}
                            </div>
                            <div
                              style={{
                                marginTop: 6,
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#2e9d58",
                              }}
                            >
                              EXACT MATCH
                            </div>
                          </div>
                        ) : ambiguous ? (
                          <div>
                            <strong>{match.candidate_count} exact candidates</strong>
                            <div className="hadmin-muted" style={{ marginTop: 4 }}>
                              Manual review required — no automatic choice.
                            </div>
                          </div>
                        ) : (
                          <span className="hadmin-muted">
                            No exact canonical match
                          </span>
                        )}
                      </td>

                      <td>{formatAdminDate(item.created_at)}</td>

                      <td>
                        <div className="hadmin-actions" style={{ flexWrap: "wrap" }}>
                          {safe && (
                            <button
                              type="button"
                              className="hadmin-btn-success"
                              disabled={bulkBusy}
                              onClick={() => quickLinkSafeMatch(item)}
                            >
                              Link Match
                            </button>
                          )}

                          <button
                            type="button"
                            className="hadmin-btn-secondary"
                            onClick={() => openItem(item)}
                            disabled={bulkBusy}
                          >
                            Review
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredItems.length > REVIEW_PAGE_SIZE && (
          <div
            className="hadmin-row-between"
            style={{ marginTop: 14, gap: 12, flexWrap: "wrap" }}
          >
            <div className="hadmin-muted" style={{ fontSize: 12 }}>
              Page {reviewPage} of {totalReviewPages} · showing {((reviewPage - 1) * REVIEW_PAGE_SIZE) + 1}–{Math.min(reviewPage * REVIEW_PAGE_SIZE, filteredItems.length)} of {filteredItems.length}
            </div>
            <div className="hadmin-actions">
              <button
                type="button"
                className="hadmin-btn-secondary"
                disabled={reviewPage <= 1}
                onClick={() => setReviewPage((page) => Math.max(1, page - 1))}
              >
                ← Previous
              </button>
              <button
                type="button"
                className="hadmin-btn-secondary"
                disabled={reviewPage >= totalReviewPages}
                onClick={() => setReviewPage((page) => Math.min(totalReviewPages, page + 1))}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </section>

      {selectedItem && (
        <>
          <div
            className="hadmin-drawer-backdrop"
            onClick={() => !busy && setSelectedItem(null)}
          />

          <aside className="hadmin-drawer">
            <div className="hadmin-drawer-header">
              <div>
                <h2>{userItemName(selectedItem)}</h2>
                <p>User item #{selectedItem.id}</p>
              </div>

              <button
                type="button"
                className="hadmin-icon-btn"
                disabled={busy}
                onClick={() => setSelectedItem(null)}
              >
                ×
              </button>
            </div>

            <div className="hadmin-drawer-body">
              {userItemImages(selectedItem).length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <div
                    className="hadmin-row-between"
                    style={{ marginBottom: 9, gap: 10 }}
                  >
                    <div>
                      <strong>Submitted Images</strong>
                      <div
                        className="hadmin-muted"
                        style={{ marginTop: 3, fontSize: 11 }}
                      >
                        Front/back and any additional user images. Click any image to enlarge.
                      </div>
                    </div>

                    <span className="hadmin-pill">
                      {userItemImages(selectedItem).length} image
                      {userItemImages(selectedItem).length === 1 ? "" : "s"}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: 10,
                    }}
                  >
                    {userItemImages(selectedItem).map((image, index) => (
                      <button
                        key={`${image.image_url}-${image.position ?? index}`}
                        type="button"
                        onClick={() =>
                          openLightbox(userItemImages(selectedItem), index)
                        }
                        title={`Click to enlarge ${reviewImageLabel(index).toLowerCase()} image`}
                        style={{
                          position: "relative",
                          padding: 0,
                          overflow: "hidden",
                          borderRadius: 10,
                          border: "1px solid rgba(255,255,255,.12)",
                          background: "#0b1017",
                          cursor: "zoom-in",
                          minHeight: 190,
                        }}
                      >
                        <img
                          src={image.image_url}
                          alt={`${userItemName(selectedItem)} ${reviewImageLabel(index)}`}
                          loading="lazy"
                          decoding="async"
                          style={{
                            display: "block",
                            width: "100%",
                            height: 220,
                            objectFit: "contain",
                            background: "#0b1017",
                          }}
                        />

                        <div
                          style={{
                            position: "absolute",
                            left: 8,
                            bottom: 8,
                            padding: "4px 8px",
                            borderRadius: 999,
                            background: "rgba(0,0,0,.72)",
                            color: "#fff",
                            fontSize: 11,
                            fontWeight: 800,
                            pointerEvents: "none",
                          }}
                        >
                          {reviewImageLabel(index)} · Click to enlarge
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {isSafeMatch(matchMap[itemKey(selectedItem.id)]) && (() => {
                const exactMatch = matchMap[itemKey(selectedItem.id)];

                return (
                  <div
                    className="hadmin-success"
                    style={{ marginBottom: 18, padding: 12 }}
                  >
                    <div
                      className="hadmin-row-between"
                      style={{ gap: 12, alignItems: "center", flexWrap: "wrap" }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <strong>Exact canonical match</strong>
                        <div style={{ marginTop: 5 }}>
                          <strong>{exactMatch.canonical_name}</strong>
                        </div>
                        <div style={{ marginTop: 3, fontSize: 12 }}>
                          {[
                            exactMatch.canonical_year,
                            exactMatch.canonical_brand,
                            exactMatch.canonical_set_name,
                            exactMatch.canonical_subset,
                          ]
                            .filter(Boolean)
                            .join(" • ")}
                          {" · "}#{exactMatch.canonical_card_number ?? "—"}
                          {" · "}canonical #{exactMatch.canonical_item_id}
                        </div>
                      </div>

                      <button
                        type="button"
                        className="hadmin-btn-success"
                        disabled={busy || bulkBusy}
                        onClick={() =>
                          linkToCanonical({
                            id: exactMatch.canonical_item_id,
                            name: exactMatch.canonical_name,
                            year: exactMatch.canonical_year,
                            brand: exactMatch.canonical_brand,
                            set_name: exactMatch.canonical_set_name,
                            subset: exactMatch.canonical_subset,
                            card_number: exactMatch.canonical_card_number,
                          })
                        }
                      >
                        Link Exact Match
                      </button>
                    </div>
                  </div>
                );
              })()}

              {!isSafeMatch(matchMap[itemKey(selectedItem.id)]) &&
                nearMatchMap[itemKey(selectedItem.id)]?.length > 0 && (
                  <div className="hadmin-info" style={{ marginBottom: 18 }}>
                    <strong>Related canonical cards found</strong>
                    <div style={{ marginTop: 7, fontSize: 12 }}>
                      These are diagnostics only — they are never auto-linked unless the
                      full canonical identity matches.
                    </div>

                    <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                      {nearMatchMap[itemKey(selectedItem.id)].map((candidate) => (
                        <div
                          key={candidate.canonical_item_id}
                          style={{
                            padding: 9,
                            border: "1px solid rgba(255,255,255,.10)",
                            borderRadius: 8,
                          }}
                        >
                          <strong>{candidate.canonical_name}</strong>
                          <div className="hadmin-muted" style={{ marginTop: 3 }}>
                            {[
                              candidate.canonical_year,
                              candidate.canonical_brand,
                              candidate.canonical_set_name,
                              candidate.canonical_subset,
                            ]
                              .filter(Boolean)
                              .join(" • ")}
                            {" · "}#{candidate.canonical_card_number ?? "—"}
                          </div>
                          {candidate.canonical_release_name && candidate.canonical_release_name !== candidate.canonical_subset && (
                            <div className="hadmin-muted" style={{ marginTop: 3, fontSize: 11 }}>
                              Modeled release: {candidate.canonical_release_name}
                            </div>
                          )}
                          <div className="hadmin-muted" style={{ marginTop: 3, fontSize: 11 }}>
                            {candidate.reason}
                          </div>
                          <div className="hadmin-actions" style={{ marginTop: 8 }}>
                            <button
                              type="button"
                              className="hadmin-btn-success"
                              disabled={busy}
                              onClick={() =>
                                linkToCanonical({
                                  id: candidate.canonical_item_id,
                                  name: candidate.canonical_name,
                                  year: candidate.canonical_year,
                                  brand: candidate.canonical_brand,
                                  set_name: candidate.canonical_set_name,
                                  subset: candidate.canonical_subset,
                                  card_number: candidate.canonical_card_number,
                                })
                              }
                            >
                              Link & Correct
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              <div className="hadmin-section-heading">
                <div>
                  <h3>Link Existing Canonical Item</h3>
                  <p>
                    Search the shared catalog when the automatic exact matcher did
                    not find a safe unique result.
                  </p>
                </div>
              </div>

              <div className="hadmin-toolbar">
                <input
                  className="hadmin-input hadmin-search"
                  value={candidateSearch}
                  onChange={(event) => setCandidateSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") searchCanonical();
                  }}
                  placeholder="Search canonical name..."
                />

                <button
                  type="button"
                  className="hadmin-btn-secondary"
                  disabled={loadingCandidates}
                  onClick={searchCanonical}
                >
                  {loadingCandidates ? "Searching..." : "Search"}
                </button>
              </div>

              {candidates.length > 0 && (
                <div className="hadmin-list" style={{ marginBottom: 22 }}>
                  {candidates.map((candidate) => (
                    <div className="hadmin-list-item" key={candidate.id}>
                      <div
                        className="hadmin-row-between"
                        style={{ gap: 12 }}
                      >
                        <div>
                          <strong>{candidate.name}</strong>

                          <div
                            className="hadmin-muted"
                            style={{ marginTop: 4, fontSize: 11 }}
                          >
                            {[
                              candidate.year,
                              candidate.brand,
                              candidate.set_name,
                              candidate.subset,
                            ]
                              .filter(Boolean)
                              .join(" • ") || "No additional identity data"}
                          </div>

                          <div
                            className="hadmin-muted"
                            style={{ marginTop: 3, fontSize: 11 }}
                          >
                            Card #{candidate.card_number ?? "—"} • canonical #
                            {candidate.id}
                          </div>
                        </div>

                        <button
                          type="button"
                          className="hadmin-btn-success"
                          disabled={busy}
                          onClick={() => linkToCanonical(candidate)}
                        >
                          Link
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div
                className="hadmin-section-heading"
                style={{ marginTop: 10 }}
              >
                <div>
                  <h3>Create New Canonical Item</h3>
                  <p>
                    Use only when this really is a new canonical identity. Taxonomy
                    is selected by name but the existing IDs are still sent to the RPC.
                  </p>
                </div>
              </div>

              <div className="hadmin-form-grid">
                <div className="hadmin-form-field full">
                  <label>Name</label>
                  <input
                    className="hadmin-input"
                    value={canonicalForm.name}
                    onChange={(e) =>
                      setCanonicalForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="hadmin-form-field">
                  <label>Brand</label>
                  <input
                    className="hadmin-input"
                    value={canonicalForm.brand}
                    onChange={(e) =>
                      setCanonicalForm((prev) => ({
                        ...prev,
                        brand: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="hadmin-form-field">
                  <label>Set</label>
                  <input
                    className="hadmin-input"
                    value={canonicalForm.set_name}
                    onChange={(e) =>
                      setCanonicalForm((prev) => ({
                        ...prev,
                        set_name: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="hadmin-form-field">
                  <label>Subset / Insert</label>
                  <input
                    className="hadmin-input"
                    value={canonicalForm.subset}
                    onChange={(e) =>
                      setCanonicalForm((prev) => ({
                        ...prev,
                        subset: e.target.value,
                      }))
                    }
                    placeholder="Optional checklist family / insert"
                  />
                </div>

                <div className="hadmin-form-field">
                  <label>Year</label>
                  <input
                    className="hadmin-input"
                    type="number"
                    value={canonicalForm.year}
                    onChange={(e) =>
                      setCanonicalForm((prev) => ({
                        ...prev,
                        year: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="hadmin-form-field">
                  <label>End Year</label>
                  <input
                    className="hadmin-input"
                    type="number"
                    value={canonicalForm.year_end}
                    onChange={(e) =>
                      setCanonicalForm((prev) => ({
                        ...prev,
                        year_end: e.target.value,
                      }))
                    }
                    placeholder="Optional"
                  />
                </div>

                <div className="hadmin-form-field">
                  <label>Card Number</label>
                  <input
                    className="hadmin-input"
                    value={canonicalForm.card_number}
                    onChange={(e) =>
                      setCanonicalForm((prev) => ({
                        ...prev,
                        card_number: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="hadmin-form-field">
                  <label>Category</label>
                  <select
                    className="hadmin-input"
                    value={canonicalForm.category_id}
                    onChange={(e) =>
                      setCanonicalForm((prev) => ({
                        ...prev,
                        category_id: e.target.value,
                        subcategory_id: "",
                      }))
                    }
                  >
                    <option value="">— Select category —</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="hadmin-form-field">
                  <label>Subcategory</label>
                  <select
                    className="hadmin-input"
                    value={canonicalForm.subcategory_id}
                    onChange={(e) =>
                      setCanonicalForm((prev) => ({
                        ...prev,
                        subcategory_id: e.target.value,
                      }))
                    }
                  >
                    <option value="">— Select subcategory —</option>
                    {formSubcategories.map((subcategory) => (
                      <option key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="hadmin-form-field full">
                  <label>Canonical Additional Details</label>
                  <textarea
                    className="hadmin-textarea"
                    value={canonicalForm.additional_details}
                    onChange={(e) =>
                      setCanonicalForm((prev) => ({
                        ...prev,
                        additional_details: e.target.value,
                      }))
                    }
                    placeholder="Optional canonical notes only — personal item notes are not copied."
                  />
                </div>
              </div>

              <h3 style={{ marginTop: 22 }}>Personal Item Data</h3>
              <pre className="hadmin-json">
                {JSON.stringify(selectedItem, null, 2)}
              </pre>
            </div>

            <div className="hadmin-drawer-footer">
              <button
                type="button"
                className="hadmin-btn-secondary"
                disabled={busy}
                onClick={() => setSelectedItem(null)}
              >
                Close
              </button>

              <button
                type="button"
                className="hadmin-btn-secondary"
                disabled={busy}
                onClick={denyItem}
                style={{ borderColor: "#c44545", color: "#c44545" }}
              >
                {busy ? "Working..." : "Deny"}
              </button>

              <button
                type="button"
                className="hadmin-btn-success"
                disabled={busy}
                onClick={createCanonicalAndLink}
              >
                {busy ? "Working..." : "Create Canonical & Approve"}
              </button>
            </div>
          </aside>
        </>
      )}

      {lightboxImages.length > 0 && lightboxImages[lightboxIndex] && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Submitted item image viewer"
          onClick={closeLightbox}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            background: "rgba(0,0,0,.92)",
          }}
        >
          <button
            type="button"
            aria-label="Close enlarged image"
            onClick={closeLightbox}
            style={{
              position: "fixed",
              top: 18,
              right: 22,
              zIndex: 100002,
              width: 44,
              height: 44,
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,.25)",
              background: "rgba(20,25,34,.9)",
              color: "#fff",
              fontSize: 28,
              lineHeight: 1,
              cursor: "pointer",
            }}
          >
            ×
          </button>

          {lightboxImages.length > 1 && (
            <button
              type="button"
              aria-label="Previous image"
              onClick={(event) => {
                event.stopPropagation();
                showPreviousLightboxImage();
              }}
              style={{
                position: "fixed",
                left: 22,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 100002,
                width: 48,
                height: 58,
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,.25)",
                background: "rgba(20,25,34,.9)",
                color: "#fff",
                fontSize: 30,
                cursor: "pointer",
              }}
            >
              ‹
            </button>
          )}

          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              maxWidth: "calc(100vw - 150px)",
              maxHeight: "calc(100vh - 70px)",
            }}
          >
            <img
              src={lightboxImages[lightboxIndex].image_url}
              alt={`${userItemName(selectedItem)} enlarged`}
              style={{
                display: "block",
                maxWidth: "100%",
                maxHeight: "calc(100vh - 120px)",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                borderRadius: 8,
                boxShadow: "0 20px 60px rgba(0,0,0,.55)",
              }}
            />

            <div
              style={{
                marginTop: 12,
                padding: "7px 12px",
                borderRadius: 999,
                background: "rgba(20,25,34,.9)",
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {reviewImageLabel(lightboxIndex)}
              {lightboxImages.length > 1 &&
                ` · ${lightboxIndex + 1} of ${lightboxImages.length}`}
              {" · Esc to close"}
            </div>
          </div>

          {lightboxImages.length > 1 && (
            <button
              type="button"
              aria-label="Next image"
              onClick={(event) => {
                event.stopPropagation();
                showNextLightboxImage();
              }}
              style={{
                position: "fixed",
                right: 22,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 100002,
                width: 48,
                height: 58,
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,.25)",
                background: "rgba(20,25,34,.9)",
                color: "#fff",
                fontSize: 30,
                cursor: "pointer",
              }}
            >
              ›
            </button>
          )}
        </div>
      )}
    </div>
  );
}
